import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, UserPlus, Copy, Trash2, Shield, Loader2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ROLE_OPTIONS = [
  { value: "account_admin", label: "Account Admin" },
  { value: "driver", label: "Driver / User" },
  { value: "viewer", label: "Viewer (read-only)" },
  { value: "accountant", label: "Accountant" },
];

export default function MembersPanel() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "driver" });
  const [lastInviteUrl, setLastInviteUrl] = useState("");
  const [permMember, setPermMember] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/members");
      setMembers(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/members/invite", inviteForm);
      const base = window.location.origin;
      const url = `${base}${data.invite_url}`;
      setLastInviteUrl(url);
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Invitation created — link copied to clipboard");
      setInviteForm({ email: "", name: "", role: "driver" });
      await load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (m, role) => {
    try {
      await api.patch(`/members/${m.id}`, { role });
      toast.success("Role updated");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const suspend = async (m) => {
    try {
      const next = m.status === "suspended" ? "active" : "suspended";
      await api.patch(`/members/${m.id}`, { status: next });
      toast.success(next === "suspended" ? "Member suspended" : "Member reactivated");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/members/${m.id}`);
      toast.success("Member removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="members-panel">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="ft-overline">TEAM · ACCOUNT MEMBERS</p>
          <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Members &amp; Invitations</h2>
          <p className="text-sm text-[#A3A3A3] mt-1">
            Free plan allows up to 5 invited users. Paid plans start at 20.
          </p>
        </div>
        <button
          onClick={() => setInviteOpen(true)}
          className="bg-[#FF5A1F] text-black px-4 py-2 font-bold inline-flex items-center gap-2 hover:bg-[#E04F1A] transition-colors"
          data-testid="invite-open"
        >
          <UserPlus size={16} /> Invite member
        </button>
      </div>

      {lastInviteUrl && (
        <div className="ft-card p-4 flex items-center gap-3 border-l-4 border-[#FF5A1F]" data-testid="last-invite-link">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[#A3A3A3] font-mono">Latest invitation link (share manually until email delivery is wired):</p>
            <p className="text-sm text-white font-mono truncate mt-1">{lastInviteUrl}</p>
          </div>
          <button
            onClick={() => { navigator.clipboard.writeText(lastInviteUrl); toast.success("Copied"); }}
            className="border border-[#262626] px-3 py-2 hover:bg-[#141414] hover:border-[#FF5A1F]/50 inline-flex items-center gap-2 text-xs"
          >
            <Copy size={12} /> Copy
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-[#737373] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : (
        <div className="ft-card p-0 overflow-hidden">
          <table className="w-full" data-testid="members-table">
            <thead>
              <tr className="border-b border-[#262626] text-left">
                <th className="ft-overline py-3 px-4">MEMBER</th>
                <th className="ft-overline py-3 px-4">ROLE</th>
                <th className="ft-overline py-3 px-4">STATUS</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-b border-[#262626] hover:bg-[#141414]">
                  <td className="py-3 px-4">
                    <p className="font-mono text-white text-sm">{m.email}</p>
                    <p className="text-xs text-[#737373] mt-0.5">{m.name || "—"}</p>
                  </td>
                  <td className="py-3 px-4">
                    {m.role === "account_owner" ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#FF5A1F]">
                        <Shield size={12} /> Owner
                      </span>
                    ) : (
                      <select
                        value={m.role}
                        onChange={(e) => changeRole(m, e.target.value)}
                        className="bg-[#0A0A0A] border border-[#262626] text-white text-sm px-2 py-1 font-mono"
                        data-testid={`role-select-${m.id}`}
                      >
                        {ROLE_OPTIONS.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs font-mono uppercase tracking-wider ${m.status === "active" ? "text-emerald-400" : "text-red-400"}`}>
                      {m.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right space-x-2">
                    {m.role !== "account_owner" && m.id !== user?.id && (
                      <>
                        <button
                          onClick={() => suspend(m)}
                          className="text-xs border border-[#262626] px-2 py-1 hover:bg-[#141414] hover:border-[#FF5A1F]/50"
                          data-testid={`suspend-${m.id}`}
                        >
                          {m.status === "suspended" ? "Reactivate" : "Suspend"}
                        </button>
                        <button
                          onClick={() => setPermMember(m)}
                          className="text-xs border border-[#262626] px-2 py-1 hover:bg-[#141414] hover:border-[#FF5A1F]/50"
                          data-testid={`edit-perms-${m.id}`}
                        >
                          Permissions
                        </button>
                        <button
                          onClick={() => remove(m)}
                          className="text-xs border border-[#262626] px-2 py-1 text-[#A3A3A3] hover:text-red-400 hover:border-red-500/50 inline-flex items-center gap-1"
                          data-testid={`remove-${m.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inviteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur p-6" onClick={() => setInviteOpen(false)}>
          <form
            onSubmit={invite}
            onClick={(e) => e.stopPropagation()}
            className="ft-card p-6 w-full max-w-md space-y-4"
            data-testid="invite-form"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold">Invite a member</h3>
              <button type="button" onClick={() => setInviteOpen(false)} className="text-[#A3A3A3] hover:text-white"><X size={18} /></button>
            </div>
            <div>
              <label className="ft-overline block mb-1">Email</label>
              <input
                type="email"
                required
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 font-mono text-white focus:border-[#FF5A1F] outline-none"
                data-testid="invite-email"
              />
            </div>
            <div>
              <label className="ft-overline block mb-1">Name (optional)</label>
              <input
                type="text"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 font-mono text-white focus:border-[#FF5A1F] outline-none"
                data-testid="invite-name"
              />
            </div>
            <div>
              <label className="ft-overline block mb-1">Role</label>
              <select
                value={inviteForm.role}
                onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 font-mono text-white focus:border-[#FF5A1F] outline-none"
                data-testid="invite-role"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-[#FF5A1F] text-black font-bold px-5 py-3 hover:bg-[#E04F1A] disabled:opacity-60 inline-flex items-center justify-center gap-2"
              data-testid="invite-submit"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Create invitation
            </button>
          </form>
        </div>
      )}

      {permMember && (
        <PermissionsModal
          member={permMember}
          onClose={() => setPermMember(null)}
          onSaved={() => { setPermMember(null); load(); }}
        />
      )}
    </div>
  );
}

function PermissionsModal({ member, onClose, onSaved }) {
  const [catalog, setCatalog] = useState({ permissions: [], defaults_by_role: {} });
  const [vehicles, setVehicles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/members/permissions").then((r) => setCatalog(r.data));
    api.get("/vehicles").then((r) => setVehicles(r.data || []));
    const defaultPerms = (member?.permissions && member.permissions.length)
      ? member.permissions
      : [];
    setPerms(defaultPerms);
    setAssigned(member?.assigned_vehicle_ids || []);
  }, [member]);

  useEffect(() => {
    if (perms.length === 0 && catalog.defaults_by_role && member?.role) {
      const d = catalog.defaults_by_role[member.role] || [];
      setPerms(d);
    }
  }, [catalog, member, perms.length]);

  const toggle = (p) => setPerms((cur) => cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);
  const toggleVeh = (id) => setAssigned((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/members/${member.id}/permissions`, { permissions: perms, assigned_vehicle_ids: assigned });
      toast.success("Permissions updated");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="permissions-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="ft-overline">MEMBER PERMISSIONS</p>
            <h3 className="font-display text-2xl font-bold tracking-tight">{member.name || member.email}</h3>
            <p className="text-xs text-[var(--text-muted)] mt-1">Role: {member.role}</p>
          </div>
          <button onClick={onClose}><X size={18}/></button>
        </div>

        <div className="mb-5">
          <div className="ft-overline mb-2">ACTIONS</div>
          <div className="grid grid-cols-2 gap-2">
            {(catalog.permissions || []).map((p) => (
              <label key={p} className={"flex items-center gap-2 rounded-lg border p-2 text-sm cursor-pointer " + (perms.includes(p) ? "border-[#FF5A1F] bg-[#FF5A1F]/10" : "border-[var(--border-line)]") }>
                <input type="checkbox" checked={perms.includes(p)} onChange={() => toggle(p)} data-testid={`perm-${p}`} />
                <span>{p.replace(/_/g, ' ')}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <div className="ft-overline mb-2">ASSIGN VEHICLES</div>
          <p className="text-xs text-[var(--text-muted)] mb-2">Leave empty to grant access to all account vehicles.</p>
          <div className="grid grid-cols-2 gap-2">
            {vehicles.map((v) => (
              <label key={v.id} className={"flex items-center gap-2 rounded-lg border p-2 text-sm cursor-pointer " + (assigned.includes(v.id) ? "border-[#FF5A1F] bg-[#FF5A1F]/10" : "border-[var(--border-line)]") }>
                <input type="checkbox" checked={assigned.includes(v.id)} onChange={() => toggleVeh(v.id)} data-testid={`assign-${v.id}`} />
                <span className="truncate">{v.number} · {v.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-[var(--border-line)]">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-[#FF5A1F] text-white font-semibold inline-flex items-center gap-2" data-testid="save-permissions">
            {saving && <Loader2 size={14} className="animate-spin"/>} Save permissions
          </button>
        </div>
      </div>
    </div>
  );
}
